import { useState } from "react";
import i18n from "../../../language/i18n";
import {
  Icon,
  EmptyState,
  constructOutcomeActivityScoreList,
} from "../../../library";
import OutcomeScatterChart from "./OutcomeScatterChart";
import { clampTimeLine, isAtOrAboveScoreLine, quadrantScoreLine } from "../../../utilization/OutcomeQuadrantUtilization";
import CheckBoxGlyph from "../CheckBoxGlyph";
import { outcomeGraphStrongWeakStyles } from "./OutcomeGraphStrongWeakStyles";
import SubjectDescriptionNavigationModal, {
  ISubjectDescriptionNavigationTarget,
  useSubjectDescriptionLookup,
} from "./SubjectDescriptionNavigationModal";

const OutcomeGraphStrongWeak = (props: any) => {
  const {
    branchAssignmentOutcomeReportList = [],
    personalizedAssignmentOutcomeReportList = [],
    // Destek ödevi rows — a time-bearing ödev kind like the two above (published results only).
    supportAssignmentOutcomeReportList = [],
    coterieType,
    // Time-range filter is owned by the parent (the /reports filter bar) and
    // passed down, so it lives next to the "Ders" dropdown rather than here.
    timeFilter = "all",
    // The source selection, when the parent owns it. `undefined` (not `[]`) means "not controlled" —
    // an empty array is a real, if degenerate, selection and must not be read as its absence.
    // Same two-mode contract `timeFilter` above already uses; the local chips leave rather than
    // mirror the hoisted row, because this component renders ONCE PER DERS.
    visibleSourceIds,
  } = props;
  const { findSubjectDescriptionByOutcomeCode } = useSubjectDescriptionLookup();
  const timeFilterWithN = timeFilter;
  const [showScatterChart, setShowScatterChart] = useState(false);
  const [showBranchAssignment, setShowBranchAssignment] = useState(true);
  const [showPersonalizedAssignment, setShowPersonalizedAssignment] =
    useState(true);
  const [showSupportAssignment, setShowSupportAssignment] = useState(true);
  // "Detay gör" is deliberately NOT part of this: it toggles a scatter VIEW, not a source.
  const isSourceSelectionControlled = visibleSourceIds !== undefined;
  const sourceIsOn = (sourceId: string, uncontrolledValue: boolean) =>
    isSourceSelectionControlled
      ? (visibleSourceIds || []).includes(sourceId)
      : uncontrolledValue;
  // The pending target IS the modal's open state — see SubjectDescriptionNavigationModal.
  const [subjectDescriptionTarget, setSubjectDescriptionTarget] =
    useState<ISubjectDescriptionNavigationTarget | null>(null);

  const hasBranchAssignmentData =
    branchAssignmentOutcomeReportList.length > 0;
  const hasPersonalizedAssignmentData =
    personalizedAssignmentOutcomeReportList.length > 0;
  const hasSupportAssignmentData = supportAssignmentOutcomeReportList.length > 0;
  const activeSourceCount =
    (hasBranchAssignmentData ? 1 : 0) +
    (hasSupportAssignmentData ? 1 : 0) +
    (hasPersonalizedAssignmentData ? 1 : 0);
  const hasMultipleSources = activeSourceCount > 1;

  const mergeOutcomeReportList = (reportList) => {
    if (!reportList || reportList.length === 0) return [];
    const merged = [];
    const mergeRecursive = (targetList, sourceList) => {
      for (const sourceItem of sourceList) {
        const existing = targetList.find(
          (m) => m.categoryDTO.id === sourceItem.categoryDTO.id,
        );
        if (existing) {
          const existingScores =
            existing.activityScoreDTOSet || existing.activityScoreList || [];
          const newScores =
            sourceItem.activityScoreDTOSet ||
            sourceItem.activityScoreList ||
            [];
          existing.activityScoreDTOSet = [...existingScores, ...newScores];
          existing.activityScoreList = existing.activityScoreDTOSet;
          if (
            sourceItem.subCategoryActivityAnswerDTOList &&
            sourceItem.subCategoryActivityAnswerDTOList.length > 0
          ) {
            existing.subCategoryActivityAnswerDTOList = mergeRecursive(
              existing.subCategoryActivityAnswerDTOList || [],
              sourceItem.subCategoryActivityAnswerDTOList,
            );
          }
        } else {
          targetList.push(JSON.parse(JSON.stringify(sourceItem)));
        }
      }
      return targetList;
    };
    mergeRecursive(merged, reportList);
    return merged;
  };

  const outcomeReportList = mergeOutcomeReportList([
    ...(sourceIsOn("BRANCH_ASSIGNMENT", showBranchAssignment)
      ? branchAssignmentOutcomeReportList
      : []),
    ...(sourceIsOn("SUPPORT_ASSIGNMENT", showSupportAssignment)
      ? supportAssignmentOutcomeReportList
      : []),
    ...(sourceIsOn("PERSONALIZED_ASSIGNMENT", showPersonalizedAssignment)
      ? personalizedAssignmentOutcomeReportList
      : []),
  ]);

  const filterReportListByTime = (reportList) => {
    if (!reportList || reportList.length === 0 || timeFilterWithN === "all") {
      return reportList || [];
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    const last7Days = new Date(today);
    last7Days.setDate(today.getDate() - 7);

    const last30Days = new Date(today);
    last30Days.setDate(today.getDate() - 30);

    const last90Days = new Date(today);
    last90Days.setDate(today.getDate() - 90);

    return reportList
      .map((report) => {
        const filterActivityScores = (activityScoreList) => {
          if (!activityScoreList) return [];
          const scoresArray = Array.isArray(activityScoreList)
            ? activityScoreList
            : Array.from(activityScoreList);

          return scoresArray.filter((activityScore) => {
            const scoreDate = new Date(activityScore.endAt);

            switch (timeFilterWithN) {
              case "this_month":
                return scoreDate >= thisMonthStart;
              case "last_month":
                return scoreDate >= lastMonthStart && scoreDate <= lastMonthEnd;
              case "last_7_days":
                return scoreDate >= last7Days;
              case "last_30_days":
                return scoreDate >= last30Days;
              case "last_90_days":
                return scoreDate >= last90Days;
              default:
                return true;
            }
          });
        };

        const filterSubCategories = (subCategoryList) => {
          if (!subCategoryList || subCategoryList.length === 0)
            return subCategoryList;

          return subCategoryList
            .map((subCategory) => ({
              ...subCategory,
              activityScoreList: filterActivityScores(
                subCategory.activityScoreDTOSet ||
                  subCategory.activityScoreList,
              ),
              subCategoryActivityAnswerDTOList: filterSubCategories(
                subCategory.subCategoryActivityAnswerDTOList,
              ),
            }))
            .filter(
              (subCategory) =>
                (subCategory.activityScoreList &&
                  subCategory.activityScoreList.length > 0) ||
                (subCategory.subCategoryActivityAnswerDTOList &&
                  subCategory.subCategoryActivityAnswerDTOList.length > 0),
            );
        };

        const filteredReport = {
          ...report,
          activityScoreList: filterActivityScores(
            report.activityScoreDTOSet || report.activityScoreList,
          ),
          subCategoryActivityAnswerDTOList: filterSubCategories(
            report.subCategoryActivityAnswerDTOList,
          ),
        };

        return filteredReport;
      })
      .filter(
        (report) =>
          (report.activityScoreList && report.activityScoreList.length > 0) ||
          (report.subCategoryActivityAnswerDTOList &&
            report.subCategoryActivityAnswerDTOList.length > 0),
      );
  };

  const constructOutcomeActivityScoreAverages = (outcomeActivityScoreList) => {
    let totalCount = 0;
    let totalTimeSpent = 0;

    outcomeActivityScoreList.forEach((outcomeActivityScore) => {
      const { count, timeSpent } = outcomeActivityScore;
      totalCount += count;
      totalTimeSpent += timeSpent;
    });

    let averageTimeSpent;
    if (totalCount !== 0) {
      averageTimeSpent = totalTimeSpent / totalCount / 1000;
    }

    return {
      // P5: the score axis is the backend mastery line, not the class average.
      averageScore: totalCount !== 0 ? quadrantScoreLine() : 0,
      averageTimeSpent: clampTimeLine(averageTimeSpent),
    };
  };

  const constructNeedSupportOutcomeList = () => {
    if (!outcomeActivityScoreAverages.averageScore) return;
    if (!outcomeActivityScoreAverages.averageTimeSpent) return;
    return filteredOutcomeActivityScoreList
      .filter(
        (outcomeActivityScore) =>
          outcomeActivityScore.timeSpent / outcomeActivityScore.count / 1000 >
            outcomeActivityScoreAverages.averageTimeSpent &&
          !isAtOrAboveScoreLine(
            (outcomeActivityScore.score / outcomeActivityScore.count) * 100,
          ),
      )
      .sort((a, b) => (a.score / a.count) * 100 - (b.score / b.count) * 100);
  };

  const constructNeedMoreWorkOutcomeList = () => {
    if (!outcomeActivityScoreAverages.averageScore) return;
    if (!outcomeActivityScoreAverages.averageTimeSpent) return;
    return filteredOutcomeActivityScoreList
      .filter(
        (outcomeActivityScore) =>
          outcomeActivityScore.timeSpent / outcomeActivityScore.count / 1000 <
            outcomeActivityScoreAverages.averageTimeSpent &&
          !isAtOrAboveScoreLine(
            (outcomeActivityScore.score / outcomeActivityScore.count) * 100,
          ),
      )
      .sort((a, b) => (a.score / a.count) * 100 - (b.score / b.count) * 100);
  };

  const constructHighPotentialOutcomeList = () => {
    if (!outcomeActivityScoreAverages.averageScore) return;
    if (!outcomeActivityScoreAverages.averageTimeSpent) return;
    return filteredOutcomeActivityScoreList
      .filter(
        (outcomeActivityScore) =>
          outcomeActivityScore.timeSpent / outcomeActivityScore.count / 1000 <
            outcomeActivityScoreAverages.averageTimeSpent &&
          isAtOrAboveScoreLine(
            (outcomeActivityScore.score / outcomeActivityScore.count) * 100,
          ),
      )
      .sort((a, b) => (a.score / a.count) * 100 - (b.score / b.count) * 100);
  };

  const constructAppreciatedOutcomeList = () => {
    if (!outcomeActivityScoreAverages.averageScore) return;
    if (!outcomeActivityScoreAverages.averageTimeSpent) return;
    return filteredOutcomeActivityScoreList
      .filter(
        (outcomeActivityScore) =>
          outcomeActivityScore.timeSpent / outcomeActivityScore.count / 1000 >
            outcomeActivityScoreAverages.averageTimeSpent &&
          isAtOrAboveScoreLine(
            (outcomeActivityScore.score / outcomeActivityScore.count) * 100,
          ),
      )
      .sort((a, b) => (a.score / a.count) * 100 - (b.score / b.count) * 100);
  };

  const navigateToSubjectDescriptions = (outcome) => {
    if (!findSubjectDescriptionByOutcomeCode(outcome.code)) return;
    setSubjectDescriptionTarget({
      code: outcome.code,
      name: outcome.name,
      // The card's own average, so the preview shows the number the learner just clicked on.
      scorePercent:
        outcome.count > 0 ? (outcome.score / outcome.count) * 100 : null,
    });
  };

  const filteredBranchAssignmentReportList =
    filterReportListByTime(outcomeReportList);
  const filteredOutcomeActivityScoreList = constructOutcomeActivityScoreList(
    filteredBranchAssignmentReportList,
  );
  const outcomeActivityScoreAverages = constructOutcomeActivityScoreAverages(
    filteredOutcomeActivityScoreList,
  );
  const scatterPointList = filteredOutcomeActivityScoreList.map((item: any) => {
    const itemScore = item.count ? (item.score / item.count) * 100 : 0;
    const itemTime = item.count ? item.timeSpent / item.count / 1000 : 0;
    return {
      code: item.code,
      text: item.name,
      score: itemScore,
      time: itemTime,
    };
  });
  const scatterMaxTime = Math.max(
    30,
    Math.ceil(
      Math.max(
        ...scatterPointList.map((p) => p.time),
        outcomeActivityScoreAverages.averageTimeSpent || 0,
      ),
    ),
  );
  const needSupportOutcomeList = constructNeedSupportOutcomeList();
  const needMoreWorkOutcomeList = constructNeedMoreWorkOutcomeList();
  const highPotentialOutcomeList = constructHighPotentialOutcomeList();
  const appreciatedOutcomeList = constructAppreciatedOutcomeList();

  // The detail ("Detay gör") toggle sits on the right of the toggle bar and
  // only applies when there is corporate outcome-activity data to scatter-plot.
  const showDetailToggle =
    filteredBranchAssignmentReportList.length > 0 &&
    filteredOutcomeActivityScoreList.length > 0;

  return (
    <>
      <style>{outcomeGraphStrongWeakStyles}</style>
      {(hasMultipleSources && !isSourceSelectionControlled) || showDetailToggle ? (
        <div className="qbe-toggle-bar">
          <div className="qbe-toggle-group">
            {hasMultipleSources && !isSourceSelectionControlled ? (
              <>
                {hasBranchAssignmentData ? (
                  <div
                    className="qbe-toggle-item"
                    onClick={() => {
                      setShowBranchAssignment(!showBranchAssignment);
                    }}
                  >
                    <CheckBoxGlyph checked={showBranchAssignment} />
                    <p>{i18n.t("branch_assignment_performance")}</p>
                  </div>
                ) : null}
                {hasSupportAssignmentData ? (
                  <div
                    className="qbe-toggle-item"
                    onClick={() => {
                      setShowSupportAssignment(!showSupportAssignment);
                    }}
                  >
                    <CheckBoxGlyph checked={showSupportAssignment} />
                    <p>{i18n.t("support_assignment")}</p>
                  </div>
                ) : null}
                {hasPersonalizedAssignmentData ? (
                  <div
                    className="qbe-toggle-item"
                    onClick={() => {
                      setShowPersonalizedAssignment(
                        !showPersonalizedAssignment,
                      );
                    }}
                  >
                    <CheckBoxGlyph checked={showPersonalizedAssignment} />
                    <p>{i18n.t("personalized_assignment_performance")}</p>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          {showDetailToggle ? (
            <div
              className="qbe-detail-toggle"
              onClick={() => {
                setShowScatterChart(!showScatterChart);
              }}
            >
              <CheckBoxGlyph checked={showScatterChart} />
              <p>{i18n.t("show_detail")}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {filteredBranchAssignmentReportList.length > 0 ? (
        <>
          {filteredOutcomeActivityScoreList.length > 0 ? (
            <>
              {showScatterChart ? (
                <div className="qbe-chart-wrapper">
                  <OutcomeScatterChart
                    points={scatterPointList}
                    avgScore={outcomeActivityScoreAverages.averageScore}
                    avgTime={outcomeActivityScoreAverages.averageTimeSpent}
                    maxTime={scatterMaxTime}
                    labels={{
                      title: i18n.t("outcome_performance_scatter_chart"),
                      perf: i18n.t("outcome_score_vs_time_performance"),
                      scoreLine: i18n.t("outcome_average_score_line", { value: quadrantScoreLine() }),
                      timeLine: i18n.t("outcome_average_time_line"),
                      axisTime: i18n.t("outcome_time_spent_seconds"),
                      axisScore: i18n.t("outcome_score"),
                      score: i18n.t("avg_score"),
                      time: i18n.t("avg_time"),
                    }}
                  />
                </div>
              ) : null}

              <div className="qbe-section-row">
                <div className="qbe-panel qbe-panel-support">
                  <div className="qbe-panel-header">
                    <Icon name="warning" color="#FF6B6B" size="medium" />
                    <div>
                      <h3 className="qbe-panel-heading">
                        {i18n.t("outcomes_need_support")}
                      </h3>
                      <p className="qbe-panel-subheading">
                        {i18n.t("outcomes_need_support_desc")}
                      </p>
                    </div>
                  </div>

                  <div className="qbe-panel-scroll">
                    {needSupportOutcomeList &&
                    needSupportOutcomeList.length > 0 ? (
                      <div className="qbe-card-list">
                        {needSupportOutcomeList.map(
                          (needSupportOutcome, index) => (
                            <div
                              key={index}
                              className="qbe-card qbe-card-support"
                              onClick={() => {
                                navigateToSubjectDescriptions(
                                  needSupportOutcome,
                                );
                              }}
                            >
                              <div className="qbe-card-inner">
                                <div className="qbe-card-main">
                                  <p className="qbe-card-name">
                                    {needSupportOutcome.name}
                                  </p>
                                </div>
                                <div className="qbe-card-stats">
                                  <div className="qbe-stat-line">
                                    {i18n.t("avg_score")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        (needSupportOutcome.score /
                                          needSupportOutcome.count) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>
                                  </div>
                                  <div className="qbe-stat-line-last">
                                    {i18n.t("avg_time")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        needSupportOutcome.timeSpent /
                                        needSupportOutcome.count /
                                        1000
                                      ).toFixed(1)}
                                      s
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <EmptyState
                        compact
                        icon="checkCircle"
                        title={i18n.t("outcomes_need_no_support")}
                      />
                    )}
                  </div>
                </div>

                <div className="qbe-panel qbe-panel-more-work">
                  <div className="qbe-panel-header">
                    <Icon name="signal" color="#F0B040" size="medium" />
                    <div>
                      <h3 className="qbe-panel-heading">
                        {i18n.t("outcomes_need_more_work")}
                      </h3>
                      <p className="qbe-panel-subheading">
                        {i18n.t("outcomes_need_more_work_desc")}
                      </p>
                    </div>
                  </div>

                  <div className="qbe-panel-scroll">
                    {needMoreWorkOutcomeList &&
                    needMoreWorkOutcomeList.length > 0 ? (
                      <div className="qbe-card-list">
                        {needMoreWorkOutcomeList.map(
                          (needMoreWorkOutcome, index) => (
                            <div
                              key={index}
                              className="qbe-card qbe-card-more-work"
                              onClick={() => {
                                navigateToSubjectDescriptions(
                                  needMoreWorkOutcome,
                                );
                              }}
                            >
                              <div className="qbe-card-inner">
                                <div className="qbe-card-main">
                                  <p className="qbe-card-name">
                                    {needMoreWorkOutcome.name}
                                  </p>
                                </div>
                                <div className="qbe-card-stats">
                                  <div className="qbe-stat-line">
                                    {i18n.t("avg_score")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        (needMoreWorkOutcome.score /
                                          needMoreWorkOutcome.count) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>
                                  </div>
                                  <div className="qbe-stat-line-last">
                                    {i18n.t("avg_time")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        needMoreWorkOutcome.timeSpent /
                                        needMoreWorkOutcome.count /
                                        1000
                                      ).toFixed(1)}
                                      s
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <EmptyState
                        compact
                        icon="checkCircle"
                        title={i18n.t("outcomes_need_no_more_work")}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="qbe-section-row">
                <div className="qbe-panel qbe-panel-appreciated">
                  <div className="qbe-panel-header">
                    <Icon name="star" color="#ACD747" size="medium" />
                    <div>
                      <h3 className="qbe-panel-heading">
                        {i18n.t("outcomes_appreciated")}
                      </h3>
                      <p className="qbe-panel-subheading">
                        {i18n.t("outcomes_appreciated_desc")}
                      </p>
                    </div>
                  </div>
                  <div className="qbe-panel-scroll">
                    {appreciatedOutcomeList &&
                    appreciatedOutcomeList.length > 0 ? (
                      <div className="qbe-card-list">
                        {appreciatedOutcomeList.map(
                          (appreciatedOutcome, index) => (
                            <div
                              key={index}
                              className="qbe-card qbe-card-appreciated"
                              onClick={() => {
                                navigateToSubjectDescriptions(
                                  appreciatedOutcome,
                                );
                              }}
                            >
                              <div className="qbe-card-inner">
                                <div className="qbe-card-main">
                                  <p className="qbe-card-name">
                                    {appreciatedOutcome.name}
                                  </p>
                                </div>
                                <div className="qbe-card-stats">
                                  <div className="qbe-stat-line">
                                    {i18n.t("avg_score")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        (appreciatedOutcome.score /
                                          appreciatedOutcome.count) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>
                                  </div>
                                  <div className="qbe-stat-line-last">
                                    {i18n.t("avg_time")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        appreciatedOutcome.timeSpent /
                                        appreciatedOutcome.count /
                                        1000
                                      ).toFixed(1)}
                                      s
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <EmptyState
                        compact
                        icon="star"
                        title={i18n.t("outcomes_no_appreciated")}
                      />
                    )}
                  </div>
                </div>

                <div className="qbe-panel qbe-panel-high-potential">
                  <div className="qbe-panel-header">
                    <Icon name="trophy" color="#54BBCB" size="medium" />
                    <div>
                      <h3 className="qbe-panel-heading">
                        {i18n.t("outcomes_high_potential")}
                      </h3>
                      <p className="qbe-panel-subheading">
                        {i18n.t("outcomes_high_potential_desc")}
                      </p>
                    </div>
                  </div>

                  <div className="qbe-panel-scroll">
                    {highPotentialOutcomeList &&
                    highPotentialOutcomeList.length > 0 ? (
                      <div className="qbe-card-list">
                        {highPotentialOutcomeList.map(
                          (highPotentialOutcome, index) => (
                            <div
                              key={index}
                              className="qbe-card qbe-card-high-potential"
                              onClick={() => {
                                navigateToSubjectDescriptions(
                                  highPotentialOutcome,
                                );
                              }}
                            >
                              <div className="qbe-card-inner">
                                <div className="qbe-card-main">
                                  <p className="qbe-card-name">
                                    {highPotentialOutcome.name}
                                  </p>
                                </div>
                                <div className="qbe-card-stats">
                                  <div className="qbe-stat-line">
                                    {i18n.t("avg_score")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        (highPotentialOutcome.score /
                                          highPotentialOutcome.count) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>
                                  </div>
                                  <div className="qbe-stat-line-last">
                                    {i18n.t("avg_time")}:{" "}
                                    <span className="qbe-stat-value">
                                      {(
                                        highPotentialOutcome.timeSpent /
                                        highPotentialOutcome.count /
                                        1000
                                      ).toFixed(1)}
                                      s
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <EmptyState
                        compact
                        icon="trophyLine"
                        title={i18n.t("outcomes_no_high_potential")}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <EmptyState compact icon="chartBar" title={i18n.t("no_report_data_for_lesson")} />
      )}
      <SubjectDescriptionNavigationModal
        target={subjectDescriptionTarget}
        coterieType={coterieType}
        onClose={() => setSubjectDescriptionTarget(null)}
      />
    </>
  );
};

export default OutcomeGraphStrongWeak;
